import {
	normalize,
	currentResource,
	localStoreFiltersForFinancialCodes,
	localStoreFiltersForCanvasEngagements,
	localStoreFiltersForClients
} from '../../common/utils/utils';
import {
	CurrencyCode,
	projectAggregationTypes,
	storageKeys
} from '../../common/utils/uiconstants';
import {API_RESOURCES, createApiReadAction} from '../../common/utils/api';
import {cloneDeep, uniqBy} from 'lodash';
import {saveStorage, clearItem} from '../../common/localStorage';

export const initialState = {
	isTMDataAvailable: false,
	isSSLDataAvailable: false,
	filterDataAvailable: false,
	areNamesIncluded: false,
	areRanksIncluded: false,
	areExpenseDetailsIncluded: false,
	expenseFiltersAvailable: false,
	expenseDetailsAvailable: false
};

export const GET_BY_ID = 'projects/get/byId';
export const GET_FINANCIAL_BY_ID = 'projects/get/ById/financial';
export const GET_BILLING_DETAILS_BY_ID = 'projects/get/ById/billing';
export const SET_BUDGET_SUMMARY_FILTER = 'projects/filter/set';
export const GET_FINANCIAL_DETAILS_BY_ID = 'projects/getFinancialDetailsById';
export const GET_FINANCIAL_DATA =
	'projects/getFinancialDetailsByIdBySubServiceLine';
export const GET_FINANCIAL_FILTERS = 'projects/getFinancialFilters';
export const GET_EXPENSE_FILTERS = 'projects/getExpenseFilters';
export const INCLUDE_NAME_INFO = 'projects/includeNameInfo';
export const RESET_FINANCIAL_DATA = 'projects/resetFinancialDetailsData';
export const GET_PROJECT_ALL_BILLING_DETAILS =
	'projects/get/ById/billingDetails';
export const GET_PROJECT_FINANCIAL_EXPENSE_DETAILS =
	'/Projects/get/ById/financialExpenseDetail';
export const INCLUDE_LOCALIZATION_EXPENSE_DETAILS =
	'/Projects/FinancialExpenseDetail/IncludeLocalization';

export default function reducer(state = initialState, action) {
	switch (action.type) {
		case GET_BY_ID: {
			if (action.error) {
				return state;
			}

			const projectId = action.response.projectId;
			const response = action.response;
			const canvasEngagements = response?.canvasEngagements ?? [];
			const clients = response?.clients ?? [];
			const financialCodes = response?.financialCodes ?? [];
			const users = response?.users ?? [];

			const canvasEngagementFilter = constructCanvasEngagementFilter(
				canvasEngagements
			);

			const canvasEngagementByFinancialCode = transformCanvasEngagements(
				canvasEngagements
			);

			const engagementsByClients = constructEngagementsByClient(
				canvasEngagements
			);

			const clientKey = `${storageKeys.projectRailFilterKey}${projectId}${storageKeys.clientsKey}`;
			const financialCodeKey = `${storageKeys.projectRailFilterKey}${projectId}${storageKeys.financialCodesKey}`;
			const canvasEngagementKey = `${storageKeys.projectRailFilterKey}${projectId}${storageKeys.canvasEngagementsKey}`;
			const projectKey = `${storageKeys.projectRailFilterKey}${projectId}`;

			return {
				...state,
				projects: {
					byId: {
						[projectId]: {
							...response,
							id: response.projectId,
							clients: clients.map(({financialClientId}) => financialClientId),
							canvasEngagements: canvasEngagements.map(
								({engagementId}) => engagementId
							),
							canvasEngagementFinancialCodeMap: canvasEngagementByFinancialCode.map(
								({id}) => id
							),
							financialCodes: financialCodes.map(
								({financialEngagementId}) => financialEngagementId
							),
							users: users.map(({userId}) => userId)
						}
					},
					allIds: [projectId]
				},
				clients: normalize(clients, 'financialClientId', undefined, true),
				financialCodes: normalize(
					financialCodes,
					'financialEngagementId',
					undefined,
					true
				),
				canvasEngagements: normalize(
					canvasEngagements,
					'engagementId',
					undefined,
					true
				),
				canvasEngagementFinancialCodeMap: normalize(
					canvasEngagementByFinancialCode,
					'id'
				),
				engagementsByClients: normalize(engagementsByClients, 'id'),
				users: normalize(users, 'userId', undefined, true),
				filter: {
					clients: localStoreFiltersForClients(
						clients,
						({financialClientId}) => financialClientId,
						clientKey
					),
					financialCodes: localStoreFiltersForFinancialCodes(
						financialCodes,
						({financialEngagementId}) => financialEngagementId,
						financialCodeKey,
						clientKey
					),
					canvasEngagements: localStoreFiltersForCanvasEngagements(
						canvasEngagementFilter,
						({id}) => id,
						canvasEngagementKey,
						projectKey,
						canvasEngagementByFinancialCode,
						response
					)
				}
			};
		}

		case GET_FINANCIAL_BY_ID: {
			return {
				...state,
				header: action.response?.financialSummary
			};
		}

		case GET_BILLING_DETAILS_BY_ID: {
			return {
				...state,
				billing: action.response
			};
		}

		case GET_PROJECT_ALL_BILLING_DETAILS: {
			return {
				...state,
				billingDetails: action.response
			};
		}

		case GET_EXPENSE_FILTERS: {
			if (action.response) {
				action.response.teamMembers &&
					action.response.teamMembers.forEach((element) => {
						element.displayName =
							element.userFirstName + ' ' + element.userLastName;
					});

				return {
					...state,
					expenseFilters: action.response,
					expenseFiltersAvailable: true
				};
			} else {
				return state;
			}
		}

		case GET_PROJECT_FINANCIAL_EXPENSE_DETAILS: {
			const expenseDetails = [];

			if (Array.isArray(action.response)) {
				action.response.forEach((item) => {
					const isGlobal = CurrencyCode.Global === item.currTypeCd;

					const globalCurrency = {costActual: 0};
					const localCurrency = {costActual: 0};

					if (isGlobal) {
						globalCurrency.costActual = item.costActual;
					} else {
						localCurrency.costActual = item.costActual;
					}

					expenseDetails.push({
						globalCurrency,
						localCurrency,
						currencyTypeCode: item.currTypeCd,
						financialEngagementId: item.finEngId,
						expenseTypeId: item.expTypeId,
						employeeGuiId: item.empGUI,
						rankCode: item.rnkCd,
						periodId: item.wkEndDt,
						subServiceLineCode: item.sslCd,
						serviceLineCode: item.slCd
					});
				});
			}

			return {
				...state,
				expenseDetails,
				expenseDetailsAvailable: true
			};
		}

		case INCLUDE_LOCALIZATION_EXPENSE_DETAILS: {
			const teamMembers = state.expenseFilters.teamMembers;
			const ranks = state.expenseFilters.ranks;
			const serviceLines = state.expenseFilters.serviceLines;
			const subServiceLines = state.expenseFilters.subServiceLines;
			const expenseTypes = state.expenseFilters.expenseTypes;

			const _expenseDetails = cloneDeep(state.expenseDetails);

			if (expenseTypes && _expenseDetails) {
				_expenseDetails?.forEach((item) => {
					const expenseTypeName = expenseTypes.find(
						(e) => e.expenseTypeId === item.expenseTypeId
					)?.expenseTypeName;

					item.expenseTypeName = expenseTypeName;
				});
			}

			if (teamMembers && ranks && _expenseDetails) {
				_expenseDetails?.forEach((item) => {
					const rankName = ranks.find((r) => r.rankCode === item.rankCode)
						?.rankName;
					const localizedRankName = currentResource.userRanks.find(
						(r) => r.id === item.rankCode
					)?.name;

					item.rankName =
						localizedRankName !== undefined ? localizedRankName : rankName;

					const teamMemberInfo = teamMembers.find(
						(tm) => tm.userGUI === item.employeeGuiId
					);
					if (teamMemberInfo) {
						item.firstName = teamMemberInfo?.userFirstName;
						item.lastName = teamMemberInfo?.userLastName;
						item.emailAddress = teamMemberInfo?.emailAddress;
						item.emailDescription = teamMemberInfo?.emailAddress;
					}
				});
			}

			if (serviceLines && subServiceLines && _expenseDetails) {
				_expenseDetails.forEach((item) => {
					const serviceLineName = serviceLines.find(
						(sl) => sl.serviceLineCode === item.serviceLineCode
					)?.serviceLineName;
					const localizedServiceLineName = currentResource.serviceLinesByServiceLineCode.find(
						(r) => r.code === item.serviceLineCode
					)?.label;
					const subServiceLineName = subServiceLines.find(
						(ssl) => ssl.subserviceLineCode === item.subServiceLineCode
					)?.subServiceLineName;

					item.serviceLineName = localizedServiceLineName || serviceLineName;
					item.subServiceLineName = subServiceLineName;
				});
			}

			return {
				...state,
				expenseDetails: _expenseDetails,
				areExpenseDetailsIncluded: true
			};
		}

		case GET_FINANCIAL_DETAILS_BY_ID:
			return {
				...state,
				financial: action.response
			};

		case GET_FINANCIAL_DATA:
			if (action.includeUsers) {
				return {
					...state,
					financialDataByTeamMember: action.response,
					areRanksIncluded: false,
					isTMDataAvailable: true
				};
			} else {
				return {
					...state,
					financialDataBySSL: action.response,
					areNamesIncluded: false,
					isSSLDataAvailable: true
				};
			}

		case GET_FINANCIAL_FILTERS: {
			if (action.response) {
				action.response.teamMembers &&
					action.response.teamMembers.forEach((element) => {
						element.displayName =
							element.userFirstName + ' ' + element.userLastName;
					});

				return {
					...state,
					financialFilters: action.response,
					filterDataAvailable: true,
					areNamesIncluded: false
				};
			} else {
				return state;
			}
		}

		case INCLUDE_NAME_INFO: {
			const financialFilters = cloneDeep(state.financialFilters);

			const serviceLines = financialFilters.serviceLines;
			const subServiceLines = financialFilters.subServiceLines;
			const teamMembers = financialFilters.teamMembers;
			const ranks = financialFilters.ranks;

			const financialData = action.isTeamMember
				? cloneDeep(state.financialDataByTeamMember)
				: cloneDeep(state.financialDataBySSL);

			if (financialData && typeof financialData.forEach === 'function') {
				financialData.forEach((element) => {
					const serviceLineName = serviceLines?.find(
						(sl) => sl.serviceLineCode === element.serviceLineCode
					)?.serviceLineName;

					const localizedServiceLineName = currentResource.serviceLinesByServiceLineCode.find(
						(r) => r.code === element.serviceLineCode
					)?.label;

					const subServiceLineName = subServiceLines?.find(
						(ssl) => ssl.subserviceLineCode === element.subserviceLineCode
					)?.subServiceLineName;

					const teamMemberInfo = teamMembers?.find(
						(tm) => tm.userGUI === element.userGUI
					);

					const rankName = ranks?.find((r) => r.rankCode === element.rankCode)
						?.rankName;

					element.serviceLineName = localizedServiceLineName || serviceLineName;
					element.subServiceLineName = subServiceLineName;
					element.firstName = teamMemberInfo?.userFirstName;
					element.lastName = teamMemberInfo?.userLastName;
					element.emailAddress = teamMemberInfo?.emailAddress;

					const localizedRankName = currentResource.userRanks.find(
						(r) => r.id === element.rankCode
					)?.name;

					element.rankName =
						localizedRankName !== undefined ? localizedRankName : rankName;
				});
			} else {
				return state;
			}

			if (action.isTeamMember) {
				return {
					...state,
					financialDataByTeamMember: financialData,
					areRanksIncluded: true
				};
			}

			return {
				...state,
				financialDataBySSL: financialData,
				areNamesIncluded: true
			};
		}

		case RESET_FINANCIAL_DATA:
			return {
				...state,
				financialDataBySSL: null,
				financialDataByTeamMember: null,
				financialFilters: null,
				isTMDataAvailable: false,
				isSSLDataAvailable: false,
				filterDataAvailable: false,
				areNamesIncluded: false,
				areRanksIncluded: false,
				areExpenseDetailsIncluded: false,
				expenseFiltersAvailable: false,
				expenseDetailsAvailable: false,
				expenseDetails: null,
				expenseFilters: null,
				billingDetails: null,
				billing: null
			};

		case SET_BUDGET_SUMMARY_FILTER: {
			let _state = {...state};

			let filter;
			if (state.filter) {
				filter = {...state.filter};
				if (filter[action.filter]) {
					filter[action.filter] = {
						byId: {
							...filter[action.filter].byId
						},
						allIds: [...filter[action.filter].allIds]
					};
				} else {
					filter[action.filter] = normalize();
				}
			} else {
				filter = {
					[action.filter]: normalize()
				};
			}

			_state.filter = filter;

			if (action.value === true) {
				if (!filter[action.filter].byId[action.key]) {
					filter[action.filter].allIds.push(action.key);
				}
				filter[action.filter].byId[action.key] = action.value;
			} else {
				delete filter[action.filter].byId[action.key];
				filter[action.filter].allIds = filter[action.filter].allIds.filter(
					(id) => id !== action.key
				);

				if (action.cleanUp) {
					Object.entries(action.cleanUp).forEach((item) => {
						const key = item[0],
							value = item[1];

						if (filter[key]) {
							filter[key] = {
								byId: {
									...filter[key].byId
								},
								allIds: filter[key].allIds.filter((id) => !value.includes(id))
							};

							for (let i = 0; i < value.length; i++) {
								if (key == 'financialCodes') {
									if (!_state.financialCodes.byId[value[i]].independenceOnly) {
										delete filter[key].byId[value[i]];
									}
								}

								if (key == 'canvasEngagements') {
									let finEngId =
										_state.canvasEngagementFinancialCodeMap.byId[value[i]]
											.financialEngagementId;
									if (!_state.financialCodes.byId[finEngId].independenceOnly) {
										delete filter[key].byId[value[i]];
									}
								}
							}

							if (!(filter[key].allIds.length > 0)) {
								delete filter[key];
							}
						}
					});
				}

				if (!(filter[action.filter].allIds.length > 0)) {
					delete filter[action.filter];
					if (!(Object.keys(filter).length > 0 && _state.filter)) {
						delete _state.filter;
					}
				}
			}

			return _state;
		}

		default:
			return state;
	}
}

export function getProjectSummary(projectId) {
	return createApiReadAction(GET_BY_ID, {
		[API_RESOURCES.PROJECTS.PROJECTS]: projectId
	});
}

export function getProjectFinancialById(
	projectId,
	aggr = projectAggregationTypes.financialEngagementId
) {
	return createApiReadAction(
		GET_FINANCIAL_BY_ID,
		{
			[API_RESOURCES.PROJECTS.PROJECTS]: projectId,
			financial: undefined
		},
		{aggr},
		{projectId}
	);
}

export function getFinancialDetails(projectId) {
	let resource = {
		[API_RESOURCES.PROJECTS.PROJECTS]: projectId,
		[API_RESOURCES.PROJECTS.FINANCIAL_DETAILS]: undefined
	};

	return createApiReadAction(GET_FINANCIAL_DETAILS_BY_ID, resource, {
		IncludeUsers: true
	});
}

export function getFinancialData(projectId, includeUsers, includeCostAmount) {
	let resource = {
		[API_RESOURCES.PROJECTS.PROJECTS]: projectId,
		[API_RESOURCES.PROJECTS.FINANCIAL_DETAILS]: undefined
	};

	return createApiReadAction(
		GET_FINANCIAL_DATA,
		resource,
		{
			includeUsers,
			includeCostAmount
		},
		{
			includeUsers,
			includeCostAmount
		}
	);
}

export function getFinancialFilters(projectId) {
	let resource = {
		[API_RESOURCES.PROJECTS.PROJECTS]: projectId,
		[API_RESOURCES.PROJECTS.FINANCIAL_FILTERS]: undefined
	};

	return createApiReadAction(GET_FINANCIAL_FILTERS, resource);
}

export function getExpenseFilters(projectId) {
	let resource = {
		[API_RESOURCES.PROJECTS.PROJECTS]: projectId,
		[API_RESOURCES.PROJECTS.EXPENSE_FILTERS]: undefined
	};

	return createApiReadAction(GET_EXPENSE_FILTERS, resource);
}

export function includeNameInfo(isTeamMember) {
	return {type: INCLUDE_NAME_INFO, isTeamMember};
}

export function resetFinancialData() {
	return {type: RESET_FINANCIAL_DATA};
}

export function getProjectAllBillings(projectId) {
	return createApiReadAction(GET_BILLING_DETAILS_BY_ID, {
		[API_RESOURCES.PROJECTS.PROJECTS]: projectId,
		[API_RESOURCES.PROJECTS.BILLING]: undefined
	});
}

export function getProjectAllBillingDetails(projectId) {
	return createApiReadAction(GET_PROJECT_ALL_BILLING_DETAILS, {
		[API_RESOURCES.PROJECTS.PROJECTS]: projectId,
		[API_RESOURCES.PROJECTS.BILLING_DETAILS]: undefined
	});
}

export function getProjectExpenseDetails(projectId) {
	return createApiReadAction(GET_PROJECT_FINANCIAL_EXPENSE_DETAILS, {
		[API_RESOURCES.PROJECTS.PROJECTS]: projectId,
		[API_RESOURCES.PROJECTS.FINANCIAL_EXPENSE_DETAILS]: undefined
	});
}

export function setProjectSummaryFilter(
	projectId,
	filter,
	key,
	value,
	cleanUp = undefined
) {
	//Save the rail filter items in session if it is unchecked
	!value
		? saveStorage(
				storageKeys.projectRailFilterKey + projectId + '_' + filter + '_' + key,
				value
		  )
		: clearItem(
				storageKeys.projectRailFilterKey + projectId + '_' + filter + '_' + key
		  );
	return {
		projectId,
		type: SET_BUDGET_SUMMARY_FILTER,
		filter,
		key,
		value,
		cleanUp
	};
}

export function includeLocalizedExpenseDetails() {
	return {type: INCLUDE_LOCALIZATION_EXPENSE_DETAILS};
}

export function constructCanvasEngagementFilter(canvasEngagements) {
	let canvasEngagementFinancialCodes = canvasEngagements
		.filter((eng) => {
			if (eng.financialEngagementId === '-1') {
				return eng.independenceFinancialEngagements.length === 0;
			}

			return true;
		})
		.map((eng) => {
			return {
				id: eng.financialEngagementId + '_' + eng.engagementId
			};
		});

	canvasEngagementFinancialCodes = canvasEngagementFinancialCodes.concat(
		canvasEngagements.flatMap((eng) => {
			return eng.independenceFinancialEngagements.map((finEng) => {
				return {
					id: finEng.financialEngagementId + '_' + eng.engagementId
				};
			});
		})
	);

	return uniqBy(canvasEngagementFinancialCodes, (e) => e.id);
}

export function transformCanvasEngagements(canvasEngagements) {
	let canvasEngagementFinancialCodes = canvasEngagements
		.filter((eng) => {
			if (eng.financialEngagementId === '-1') {
				return eng.independenceFinancialEngagements.length === 0;
			}

			return true;
		})
		.map((eng) => {
			return {
				engagementId: eng.engagementId,
				financialEngagementId: eng.financialEngagementId,
				id: `${eng.financialEngagementId}_${eng.engagementId}`
			};
		});

	canvasEngagementFinancialCodes = canvasEngagementFinancialCodes.concat(
		canvasEngagements.flatMap((eng) => {
			return eng.independenceFinancialEngagements.map((finEng) => {
				return {
					engagementId: eng.engagementId,
					financialEngagementId: finEng.financialEngagementId,
					id: `${finEng.financialEngagementId}_${eng.engagementId}`
				};
			});
		})
	);

	return uniqBy(canvasEngagementFinancialCodes, (e) => e.id);
}

export function constructEngagementsByClient(canvasEngagements) {
	let engagements = canvasEngagements.map((engagement) => {
		return {
			clientName: engagement.clientName,
			financialClientId: engagement.financialClientId,
			canvasEngagements: [engagement],
			id: engagement.clientId
		};
	});

	return uniqBy(engagements, (e) => e.id);
}
